import type { AnalyticsBucket, AnalyticsRange, TopContentItem } from "@/lib/admin-api";

// Pure helpers for the Analytics page. Kept out of the component so the
// arithmetic that could quietly produce a wrong number is testable on its own.

export const ANALYTICS_RANGES: AnalyticsRange[] = ["7d", "30d", "90d"];

export const RANGE_LABEL: Record<AnalyticsRange, string> = {
  "7d": "7 days",
  "30d": "30 days",
  "90d": "90 days",
};

/**
 * Bucket width for a range: day for 7d and 30d, week for 90d.
 *
 * admin-service applies the same rule when no bucket is sent. The client
 * computes it too because it labels the axis before the response arrives, and
 * a label that changes once the data lands reads as a bug.
 */
export function bucketForRange(range: AnalyticsRange): AnalyticsBucket {
  return range === "90d" ? "week" : "day";
}

const NUMBER_FORMAT = new Intl.NumberFormat("en-GB");

export function formatCount(value: number): string {
  return NUMBER_FORMAT.format(value);
}

/**
 * A bucket boundary, in UTC.
 *
 * UTC because the buckets themselves are UTC. Rendering in the browser's zone
 * would label a bucket with a day it does not cover, and put two operators in
 * different offices on different days.
 */
export function formatBucketLabel(bucketStart: string): string {
  const date = new Date(bucketStart);
  if (Number.isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "short",
    timeZone: "UTC",
  }).format(date);
}

/**
 * What to show as a leaderboard row's primary text: the title, or the id when
 * the title is empty. The id is not a fallback label so much as the only true
 * thing left about a piece that has been deleted.
 */
export function topContentPrimaryLabel(item: TopContentItem): string {
  return item.title.trim() || item.entityId;
}

// --- Google Analytics freshness ----------------------------------------

/**
 * How old the Google Analytics figures are, as one sentence.
 *
 * admin-service serves that section from a fifteen-minute cache, and during a
 * Google outage it serves the last good read instead of emptying the section.
 * The figures are therefore routinely minutes old and occasionally much older.
 * Presenting them as live is the failure this line exists to prevent, so the
 * age is rendered rather than the timestamp: "read 3 hours ago" is read
 * correctly at a glance in a way that an ISO string beside a chart is not.
 *
 * Returns the empty string for an absent or unparseable value, and the caller
 * renders nothing rather than a placeholder.
 */
export function formatReadAge(cachedAt: string | undefined, now: Date): string {
  if (!cachedAt) return "";
  const read = new Date(cachedAt).getTime();
  if (Number.isNaN(read)) return "";

  // Clamped at zero. A cachedAt slightly in the future is clock skew between
  // this browser and the service, not a reading from the future, and "read in
  // -1 minutes" is a visible nonsense where "less than a minute" is merely
  // imprecise.
  const seconds = Math.max(Math.floor((now.getTime() - read) / 1000), 0);
  if (seconds < 60) return "Read less than a minute ago.";

  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `Read ${minutes} ${minutes === 1 ? "minute" : "minutes"} ago.`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `Read ${hours} ${hours === 1 ? "hour" : "hours"} ago.`;

  const days = Math.floor(hours / 24);
  return `Read ${days} ${days === 1 ? "day" : "days"} ago.`;
}

// --- Moderation mix ----------------------------------------------------

/**
 * The judge verdict mix.
 *
 * `verdict` is FREE TEXT server-side, not an enum, so this cannot be built by
 * asking for each verdict in turn: nothing bounds the set. Only the two known
 * values are queried, and everything else is derived as one bucket from
 * total minus the two. That derived figure is honest about what it is (an
 * "other" count) and cannot go stale when a new verdict string appears.
 */
export interface JudgeVerdictMix {
  clean: number;
  spam: number;
  /** total - clean - spam. Never negative, see below. */
  other: number;
  total: number;
}

export function deriveJudgeVerdictMix(total: number, clean: number, spam: number): JudgeVerdictMix {
  // Clamped at zero. The three counts are three separate requests and can be
  // read across a write, so clean + spam can briefly exceed the total that was
  // read first. A negative "other" would be a visible nonsense; a zero is a
  // rounding artefact of the moment it was read.
  const other = Math.max(total - clean - spam, 0);
  return { clean, spam, other, total };
}

// --- Search Console ------------------------------------------------------

/** A fraction (0..1) as a percentage string, one decimal place. */
export function formatPercent(fraction: number): string {
  if (!Number.isFinite(fraction)) return "0.0%";
  return `${(fraction * 100).toFixed(1)}%`;
}

/** Average ranking position, one decimal place. Lower is better. */
export function formatPosition(position: number): string {
  if (!Number.isFinite(position)) return "";
  return position.toFixed(1);
}

/**
 * A Search Console page URL, shortened to a path for display.
 *
 * Search Console returns the full URL; Google Analytics' own paths and the
 * read leaderboard above both read as bare paths, and the Search tables sit
 * next to them. Falls back to the full URL for a property on a different
 * domain or scheme than expected, rather than mangling it.
 */
export function searchPagePath(url: string): string {
  try {
    const parsed = new URL(url);
    return parsed.pathname + parsed.search;
  } catch {
    return url;
  }
}
